import React, { useState } from 'react';
import styled from 'styled-components';
import { formatYearDisplay } from '../../../utils/strings';
import expandedIcon from '../../../static/icons/expanded.svg';
import collapsedIcon from '../../../static/icons/collapsed.svg';
import StepLabelBadge from '../StepLabelBadge';
import MultiSelectDropdown from '../MultiSelectDropdown';

function SetupStepSection({
  availableYears,
  availableSubjects,
  availableTaskTypes,
  availableResponseTypes,
  selectedYears,
  selectedSubjects,
  selectedTaskTypes,
  selectedResponseTypes,
  onYearsChange,
  onSubjectsChange,
  onTaskTypesChange,
  onResponseTypesChange,
  getSubjectLabel,
  getSubjectValue,
  isUpdating = false,
  defaultExpanded = true,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const defaultGetSubjectLabel = (opt) => {
    if (typeof opt === 'object' && opt !== null) {
      return opt.subjectName ?? opt.name ?? opt.title ?? opt.subjectCode ?? opt.code ?? opt;
    }
    return opt;
  };

  const defaultGetSubjectValue = (opt) => {
    if (typeof opt === 'object' && opt !== null) {
      return opt.subjectCode ?? opt.code ?? opt.title ?? opt;
    }
    return opt;
  };

  const subjectLabelFn = getSubjectLabel || defaultGetSubjectLabel;
  const subjectValueFn = getSubjectValue || defaultGetSubjectValue;

  const formatSubjectsForDisplay = () => {
    return selectedSubjects
      .map((s) => (typeof s === 'object' ? subjectLabelFn(s) : s))
      .join(', ');
  };

  return (
    <StepSection>
      <StepHeader onClick={() => setIsExpanded(!isExpanded)}>
        <StepHeaderLeft>
          <StepLabelBadge>Step 1 - Set Up</StepLabelBadge>
          {!isExpanded && (
            <CollapsedBadgesRow>
              {selectedYears.length > 0 && (
                <CollapsedBadge>
                  <BadgeLabel>Years:</BadgeLabel>
                  <BadgeValue>{selectedYears.map((y) => formatYearDisplay(y)).join(', ')}</BadgeValue>
                </CollapsedBadge>
              )}
              {selectedSubjects.length > 0 && (
                <CollapsedBadge>
                  <BadgeLabel>Subjects:</BadgeLabel>
                  <BadgeValue>{formatSubjectsForDisplay()}</BadgeValue>
                </CollapsedBadge>
              )}
              {selectedTaskTypes.length > 0 && (
                <CollapsedBadge>
                  <BadgeLabel>Task Type:</BadgeLabel>
                  <BadgeValue>{selectedTaskTypes.join(', ')}</BadgeValue>
                </CollapsedBadge>
              )}
              {selectedResponseTypes.length > 0 && (
                <CollapsedBadge>
                  <BadgeLabel>Submission Type:</BadgeLabel>
                  <BadgeValue>{selectedResponseTypes.join(', ')}</BadgeValue>
                </CollapsedBadge>
              )}
            </CollapsedBadgesRow>
          )}
        </StepHeaderLeft>
        <ExpandIcon src={isExpanded ? expandedIcon : collapsedIcon} />
      </StepHeader>
      {isExpanded && (
        <StepContent>
          <SetupFieldsRow>
            <SetupField>
              <SetupFieldLabel>
                Years<RequiredStar>*</RequiredStar> <OptionalText>(start here)</OptionalText>
              </SetupFieldLabel>
              <MultiSelectDropdown
                options={availableYears}
                selectedValues={selectedYears}
                onChange={onYearsChange}
                placeholder="Select all that apply"
                getOptionLabel={(opt) => formatYearDisplay(opt)}
                getOptionValue={(opt) => opt}
                alwaysWhite
              />
            </SetupField>
            <SetupField>
              <SetupFieldLabel>
                Subjects<RequiredStar>*</RequiredStar>
              </SetupFieldLabel>
              <MultiSelectDropdown
                options={availableSubjects}
                selectedValues={selectedSubjects}
                onChange={onSubjectsChange}
                placeholder="Select all that apply"
                getOptionLabel={subjectLabelFn}
                getOptionValue={subjectValueFn}
                disabled={!isUpdating && selectedYears.length === 0}
              />
            </SetupField>
            <SetupField>
              <SetupFieldLabel>
                Task type <OptionalText>(optional)</OptionalText>
              </SetupFieldLabel>
              <MultiSelectDropdown
                options={availableTaskTypes ?? []}
                selectedValues={selectedTaskTypes}
                onChange={onTaskTypesChange}
                placeholder="Select all that apply"
                getOptionLabel={(opt) => opt?.label ?? opt?.title ?? opt}
                getOptionValue={(opt) => opt?.value ?? opt}
                disabled={!isUpdating && selectedSubjects.length === 0}
              />
            </SetupField>
            <SetupField>
              <SetupFieldLabel>
                Submission Type <OptionalText>(optional)</OptionalText>
              </SetupFieldLabel>
              <MultiSelectDropdown
                options={availableResponseTypes ?? []}
                selectedValues={selectedResponseTypes}
                onChange={onResponseTypesChange}
                placeholder="Select all that apply"
                getOptionLabel={(opt) => opt?.label ?? opt}
                getOptionValue={(opt) => opt?.value ?? opt}
                disabled={!isUpdating && selectedTaskTypes.length === 0}
              />
            </SetupField>
          </SetupFieldsRow>
        </StepContent>
      )}
    </StepSection>
  );
}

const StepSection = styled.div`
  border-bottom: 1px solid #c9c6cc80;
`;

const StepHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  background: #ffffff;

  &:hover {
    background: #fafafa;
  }
`;

const StepHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
`;

const ExpandIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const StepContent = styled.div`
  padding: 0 20px 20px 20px;
  background: #ffffff;
`;

const SetupFieldsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
`;

const SetupField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SetupFieldLabel = styled.label`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #1e252a;
`;

const RequiredStar = styled.span`
  color: #e53935;
`;

const OptionalText = styled.span`
  font-weight: 400;
  color: #6b7280;
`;

const CollapsedBadgesRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CollapsedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #F9F3FF;
  border-radius: 6px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 13px;
`;

const BadgeLabel = styled.span`
  color: #50009D;
  font-weight: 500;
`;

const BadgeValue = styled.span`
  color: #50009D;
  font-weight: 400;
`;

export default SetupStepSection;
